<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useSponsors } from '@/composables/useSponsors'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import SlideOver from '@/components/shared/SlideOver.vue'
import HeaderCreateButton from '@/components/shared/HeaderCreateButton.vue'
import type { Sponsor, SponsorFormData } from '@/types/sponsors'
import { SPONSOR_TIERS } from '@/types/sponsors'

const { t } = useI18n()
const route = useRoute()

const eventSlug = route.params['eventSlug'] as string
const editionSlug = route.params['editionSlug'] as string

const {
  sponsors,
  loading,
  saving,
  logoUploading,
  errors,
  generalError,
  fetchSponsors,
  createSponsor,
  updateSponsor,
  deleteSponsor,
  uploadSponsorLogo,
  deleteSponsorLogo,
} = useSponsors()

// Modal state
const showModal = ref(false)
const editingSponsor = ref<Sponsor | null>(null)
const showAdvanced = ref(false)

// Delete confirmation state
const deletingSponsorId = ref<number | null>(null)
const showDeleteConfirm = ref(false)

// Form state
const formData = ref<SponsorFormData>({
  name: '',
  tier: 'partner',
  website_url: null,
  sort_order: 0,
})

const isEditMode = computed(() => editingSponsor.value !== null)

// Logo upload state for inline feedback
const logoError = ref<string | null>(null)

// Group sponsors by tier in display order; skip empty tiers
const sponsorsByTier = computed(() => {
  return SPONSOR_TIERS.map((tier) => ({
    tier,
    items: sponsors.value.filter((s) => s.tier === tier),
  })).filter((group) => group.items.length > 0)
})

function tierLabel(tier: string): string {
  return t(`editions.sponsors.tiers.${tier}`)
}

function openCreateModal(): void {
  editingSponsor.value = null
  showAdvanced.value = false
  logoError.value = null
  formData.value = {
    name: '',
    tier: 'partner',
    website_url: null,
    sort_order: 0,
  }
  showModal.value = true
}

function openEditModal(sponsor: Sponsor): void {
  editingSponsor.value = sponsor
  showAdvanced.value = false
  logoError.value = null
  formData.value = {
    name: sponsor.name,
    tier: sponsor.tier,
    website_url: sponsor.website_url,
    sort_order: sponsor.sort_order,
  }
  showModal.value = true
}

function closeModal(): void {
  if (saving.value) return
  showModal.value = false
  editingSponsor.value = null
  logoError.value = null
}


async function handleSubmit(): Promise<void> {
  const payload: SponsorFormData = {
    ...formData.value,
    website_url: formData.value.website_url || null,
  }

  if (isEditMode.value && editingSponsor.value) {
    const result = await updateSponsor(eventSlug, editionSlug, editingSponsor.value.id, payload)
    if (result !== null) {
      closeModal()
    }
  } else {
    const result = await createSponsor(eventSlug, editionSlug, payload)
    if (result !== null) {
      // Immediately re-open in edit mode so the user can upload a logo
      editingSponsor.value = result
      logoError.value = null
    }
  }
}

function requestDelete(sponsor: Sponsor): void {
  deletingSponsorId.value = sponsor.id
  showDeleteConfirm.value = true
}

function cancelDelete(): void {
  deletingSponsorId.value = null
  showDeleteConfirm.value = false
}

async function confirmDelete(): Promise<void> {
  if (deletingSponsorId.value === null) return
  await deleteSponsor(eventSlug, editionSlug, deletingSponsorId.value)
  showDeleteConfirm.value = false
  deletingSponsorId.value = null
}

async function handleLogoFileChange(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file || !editingSponsor.value) return

  logoError.value = null
  const result = await uploadSponsorLogo(eventSlug, editionSlug, editingSponsor.value.id, file)
  if (result !== null) {
    editingSponsor.value = result
  } else {
    // Surface upload error from errors.logo if available
    const logoErrors = errors.value['logo']
    if (logoErrors && logoErrors.length > 0) {
      logoError.value = logoErrors[0] ?? null
    } else {
      logoError.value = t('editions.sponsors.errors.logo_upload_failed')
    }
  }
  // Reset file input so the same file can be re-selected after error
  input.value = ''
}

async function handleLogoRemove(): Promise<void> {
  if (!editingSponsor.value) return
  logoError.value = null
  const success = await deleteSponsorLogo(eventSlug, editionSlug, editingSponsor.value.id)
  if (success) {
    editingSponsor.value = { ...editingSponsor.value, logo_url: null }
  }
}

function fieldError(field: string): string | null {
  const errs = errors.value[field]
  return errs && errs.length > 0 ? (errs[0] ?? null) : null
}

function getWebsiteHostname(url: string | null): string | null {
  if (!url) return null
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

onMounted(async () => {
  await fetchSponsors(eventSlug, editionSlug)
})
</script>

<template>
  <div>
    <!-- General error banner -->
    <div
      v-if="generalError && !showModal"
      class="mb-4 rounded-xl bg-red-500/10 border border-red-500/30 px-5 py-3 text-sm text-red-400"
    >
      {{ t(generalError) }}
    </div>

    <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
      <!-- Panel header -->
      <div class="flex items-start justify-between mb-2">
        <div>
          <h2 class="text-xl font-bold text-white">{{ t('editions.sponsors.heading') }}</h2>
          <p class="text-sm text-slate-400 mt-1">{{ t('editions.sponsors.intro') }}</p>
        </div>
        <HeaderCreateButton
          class="shrink-0 ml-4"
          compact
          tight-gap
          :label="t('editions.sponsors.add_button')"
          @click="openCreateModal"
        />
      </div>

      <div class="border-t border-slate-700 mt-6 pt-6">
        <!-- Loading state -->
        <div v-if="loading" class="flex items-center justify-center py-8">
          <svg class="animate-spin h-6 w-6 text-emerald-500" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>

        <!-- Empty state -->
        <div
          v-else-if="sponsors.length === 0"
          class="rounded-xl border border-dashed border-slate-700 px-6 py-12 text-center"
        >
          <svg
            class="mx-auto mb-4 h-10 w-10 text-slate-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="1.5"
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
          <p class="text-base font-semibold text-slate-300 mb-1">{{ t('editions.sponsors.empty_title') }}</p>
          <p class="text-sm text-slate-500 mb-4">{{ t('editions.sponsors.empty_body') }}</p>
          <button
            type="button"
            class="inline-flex items-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
            @click="openCreateModal"
          >
            {{ t('editions.sponsors.empty_cta') }}
          </button>
        </div>

        <!-- Sponsors grouped by tier -->
        <div v-else class="space-y-8">
          <div
            v-for="group in sponsorsByTier"
            :key="group.tier"
          >
            <!-- Tier header -->
            <h3 class="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">
              {{ tierLabel(group.tier) }}
            </h3>

            <!-- Sponsor rows -->
            <div class="space-y-2">
              <div
                v-for="sponsor in group.items"
                :key="sponsor.id"
                class="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl bg-slate-800 border border-slate-700 px-4 py-3"
              >
                <!-- Logo thumbnail -->
                <div class="shrink-0 w-12 h-12 rounded-lg bg-slate-700 overflow-hidden flex items-center justify-center">
                  <img
                    v-if="sponsor.logo_url"
                    :src="sponsor.logo_url"
                    :alt="sponsor.name"
                    class="w-full h-full object-contain"
                  />
                  <svg
                    v-else
                    class="w-6 h-6 text-slate-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>

                <!-- Name and website -->
                <div class="flex-1 min-w-0">
                  <p class="text-sm font-semibold text-white truncate">{{ sponsor.name }}</p>
                  <a
                    v-if="sponsor.website_url"
                    :href="sponsor.website_url"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-xs text-slate-400 hover:text-emerald-400 transition-colors truncate"
                  >
                    {{ getWebsiteHostname(sponsor.website_url) }}
                  </a>
                </div>

                <!-- Tier badge -->
                <div class="shrink-0">
                  <span class="inline-block rounded-full bg-slate-700 px-2 py-0.5 text-xs font-semibold text-slate-300">
                    {{ tierLabel(sponsor.tier) }}
                  </span>
                </div>

                <!-- Sort order -->
                <div class="shrink-0 text-xs text-slate-500 w-8 text-right">
                  {{ sponsor.sort_order }}
                </div>

                <!-- Actions -->
                <div class="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    class="text-xs font-medium text-slate-300 hover:text-white bg-slate-700 hover:bg-slate-600 rounded-lg px-3 py-1.5 transition-colors"
                    @click="openEditModal(sponsor)"
                  >
                    {{ t('editions.sponsors.edit') }}
                  </button>
                  <button
                    type="button"
                    class="text-xs font-medium text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 rounded-lg px-3 py-1.5 transition-colors"
                    @click="requestDelete(sponsor)"
                  >
                    {{ t('editions.sponsors.delete') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Create / Edit Slide-over ── -->
    <SlideOver
      :open="showModal"
      :title="isEditMode ? t('editions.sponsors.form.edit_title') : t('editions.sponsors.form.create_title')"
      :saving="saving"
      @close="closeModal"
    >
      <form id="sponsor-form" novalidate class="space-y-4" @submit.prevent="handleSubmit">
        <!-- General save error -->
        <div
          v-if="generalError"
          class="rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
        >
          {{ t(generalError) }}
        </div>

        <!-- Name -->
        <div>
          <label for="sp-name" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('editions.sponsors.form.name_label') }}
          </label>
          <input
            id="sp-name"
            v-model="formData.name"
            type="text"
            :placeholder="t('editions.sponsors.form.name_placeholder')"
            class="w-full rounded-lg bg-slate-800 border px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            :class="fieldError('name') ? 'border-red-500' : 'border-slate-600'"
          />
          <p v-if="fieldError('name')" class="mt-1 text-xs text-red-400">{{ fieldError('name') }}</p>
        </div>

        <!-- Tier -->
        <div>
          <label for="sp-tier" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('editions.sponsors.form.tier_label') }}
          </label>
          <select
            id="sp-tier"
            v-model="formData.tier"
            class="w-full rounded-lg bg-slate-800 border px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            :class="fieldError('tier') ? 'border-red-500' : 'border-slate-600'"
          >
            <option v-for="tier in SPONSOR_TIERS" :key="tier" :value="tier">
              {{ tierLabel(tier) }}
            </option>
          </select>
          <p v-if="fieldError('tier')" class="mt-1 text-xs text-red-400">{{ fieldError('tier') }}</p>
        </div>

        <!-- Website URL -->
        <div>
          <label for="sp-website" class="block text-sm font-medium text-slate-300 mb-1">
            {{ t('editions.sponsors.form.website_label') }}
          </label>
          <input
            id="sp-website"
            v-model="formData.website_url"
            type="url"
            :placeholder="t('editions.sponsors.form.website_placeholder')"
            class="w-full rounded-lg bg-slate-800 border px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            :class="fieldError('website_url') ? 'border-red-500' : 'border-slate-600'"
          />
          <p v-if="fieldError('website_url')" class="mt-1 text-xs text-red-400">{{ fieldError('website_url') }}</p>
        </div>

        <!-- Advanced settings toggle -->
        <div>
          <button
            type="button"
            class="flex items-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-slate-300 transition-colors"
            @click="showAdvanced = !showAdvanced"
          >
            <svg
              class="w-3.5 h-3.5 transition-transform"
              :class="showAdvanced ? 'rotate-90' : ''"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
            {{ t('editions.sponsors.form.advanced') }}
          </button>

          <div v-if="showAdvanced" class="mt-3">
            <label for="sp-sort-order" class="block text-sm font-medium text-slate-300 mb-1">
              {{ t('editions.sponsors.form.sort_order_label') }}
            </label>
            <input
              id="sp-sort-order"
              v-model.number="formData.sort_order"
              type="number"
              min="0"
              max="10000"
              class="w-full sm:w-32 rounded-lg bg-slate-800 border border-slate-600 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
            <p class="mt-1 text-xs text-slate-500">{{ t('editions.sponsors.form.sort_order_hint') }}</p>
            <p v-if="fieldError('sort_order')" class="mt-1 text-xs text-red-400">{{ fieldError('sort_order') }}</p>
          </div>
        </div>

        <!-- Logo upload (edit mode only) -->
        <div>
          <p class="block text-sm font-medium text-slate-300 mb-2">{{ t('editions.sponsors.form.logo_label') }}</p>

          <!-- Create mode: logo disabled hint -->
          <template v-if="!isEditMode">
            <p class="text-xs text-slate-500 italic">{{ t('editions.sponsors.form.logo_hint_create') }}</p>
          </template>

          <!-- Edit mode: logo controls -->
          <template v-else>
            <!-- Current logo preview -->
            <div v-if="editingSponsor?.logo_url" class="mb-3 flex items-center gap-3">
              <img
                :src="editingSponsor.logo_url"
                :alt="editingSponsor.name"
                class="w-16 h-16 rounded-lg object-contain bg-slate-700"
              />
              <button
                type="button"
                :disabled="logoUploading"
                class="text-xs font-medium text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg px-3 py-1.5 transition-colors"
                @click="handleLogoRemove"
              >
                <span v-if="logoUploading">{{ t('editions.sponsors.form.logo_uploading') }}</span>
                <span v-else>{{ t('editions.sponsors.form.logo_remove') }}</span>
              </button>
            </div>

            <!-- File input -->
            <label
              for="sp-logo"
              class="block text-xs font-medium text-slate-400 mb-1"
            >
              {{ editingSponsor?.logo_url ? t('editions.sponsors.form.logo_change') : t('editions.sponsors.form.logo_hint_edit') }}
            </label>
            <div class="flex items-center gap-3">
              <input
                id="sp-logo"
                type="file"
                accept="image/png,image/jpeg"
                :disabled="logoUploading"
                class="block text-sm text-slate-400 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-emerald-500 file:text-white hover:file:bg-emerald-400 file:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                @change="handleLogoFileChange"
              />
              <AppSpinner v-if="logoUploading" size="button" color="text-emerald-500" />
            </div>

            <!-- Logo-specific error -->
            <p v-if="logoError" class="mt-1 text-xs text-red-400">{{ logoError }}</p>
          </template>
        </div>
      </form>

      <template #footer>
        <div class="flex items-center justify-end gap-3">
          <button
            type="button"
            :disabled="saving"
            class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
            @click="closeModal"
          >
            {{ t('editions.sponsors.form.cancel_button') }}
          </button>
          <button
            type="submit"
            form="sponsor-form"
            :disabled="saving"
            class="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
          >
            <AppSpinner v-if="saving" size="button" />
            {{ isEditMode ? t('editions.sponsors.form.submit_edit') : t('editions.sponsors.form.submit_create') }}
          </button>
        </div>
      </template>
    </SlideOver>

    <!-- ── Delete Confirmation Dialog ── -->
    <div
      v-if="showDeleteConfirm"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
    >
      <!-- Backdrop -->
      <div
        class="absolute inset-0 bg-black/70"
        @click="cancelDelete"
      />

      <!-- Dialog -->
      <div class="relative z-10 w-full max-w-sm bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl">
        <h3 class="text-base font-bold text-white mb-2">
          {{ t('editions.sponsors.delete_confirm_title') }}
        </h3>
        <p class="text-sm text-slate-400 mb-5">
          {{ t('editions.sponsors.delete_confirm_body') }}
        </p>
        <div class="flex items-center justify-end gap-3">
          <button
            type="button"
            class="text-sm font-medium text-slate-400 hover:text-white transition-colors px-4 py-2"
            @click="cancelDelete"
          >
            {{ t('editions.sponsors.delete_confirm_cancel') }}
          </button>
          <button
            type="button"
            :disabled="saving"
            class="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
            @click="confirmDelete"
          >
            <svg
              v-if="saving"
              class="animate-spin h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            {{ t('editions.sponsors.delete_confirm_action') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
